/**
 * chart / データ可視化固有の色定義 (D-1 論点 3)。
 *
 * ecloud design system の semantic token に対応が無い chart 色 (brand 系明暗色 /
 * 気象色 / カテゴリ色 等) をこの 1 ファイルに集約する。 以後、 新しい chart 色は
 * このファイル経由でのみ定義し、 個別ファイルに hex を直書きしない
 * (lint `no-restricted-syntax` の例外も本ファイルだけに限定している。
 * `frontend/packages/eslint-config/react-app.cjs` 参照)。
 *
 * `LightModeSemanticColors.*.value` で解決できる値 (= ecloud token に対応がある値)
 * はここには置かない。 各利用箇所にそのまま token 参照を残す。
 *
 * セクションはドメイン単位 (需要予測 / ガップ / ポジション / スポット価格 /
 * 発電 / コマ表示) で区切る。
 */
import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

// ============================================================
// 需要予測 (pages/DemandForecastDaily/components/ForecastChart)
// ============================================================

/** 当日実績エリア (Area fill/stroke)。 eclair token なし (≈ brand-green #69d582 の明色)。 */
export const DEMAND_FORECAST_TODAY_ACTUAL_AREA_COLOR = '#86efac'

/** 過去実績線の色 palette。 */
export const DEMAND_FORECAST_PAST_COLORS = [
  '#f59e0b', // eclair token なし (= ドメイン固有色: Tailwind amber-500)
  LightModeSemanticColors.Text.negativeMid.value,
  '#8b5cf6', // eclair ≈ brand-purple
  '#06b6d4', // eclair ≈ brand-teal
  '#ec4899', // eclair ≈ brand-pink
]

/**
 * 確定履歴参照列線の色 palette (s07-confirm-history-import-reference-view.md
 * §UI「チャート系列」)。DEMAND_FORECAST_PAST_COLORS (過去実績) とは別の青系
 * Brand token に寄せ、系列の出自 (過去実績 vs 確定履歴) を色相で区別できるようにする。
 */
export const DEMAND_FORECAST_CONFIRM_RECORD_REF_COLORS = [
  LightModeSemanticColors.Brand.cyan.value,
  LightModeSemanticColors.Brand.blue.value,
  LightModeSemanticColors.Brand.green.value,
  LightModeSemanticColors.Brand.yellow.value,
  LightModeSemanticColors.Brand.red.value,
]

/**
 * 需要想定詳細チャートの系列色の**単一 SoT**。 チャートの描画色 (ForecastChart の
 * `<Line>` / `<Area>`) と凡例 swatch の色 (ForecastCard が ForecastChartLegend へ
 * 渡す `colors`) が **同一の値を参照する**ための定義。
 *
 * 2026-08-31 是正: 凡例側 (`ForecastCard`) が同じ色を raw hex で**二重に**持って
 * おり、 チャート側と食い違っていた (気温予測 `#dc2626` vs `Text.negativeMid` /
 * 過去実績 2 本目 `#ef4444` vs `Text.negativeMid`)。 凡例は「線の色の見本」なので、
 * ズレると凡例として機能しない。 同じ drift を先に潰した
 * `BALANCE_DEMAND_SERIES_COLORS` (下記) と同じ構造にする。
 *
 * **ここに無い色を各ファイルで直書きしない。** 増やす時はこの定数に足す。
 */
export const DEMAND_FORECAST_SERIES_COLORS = {
  /** 需要想定 (編集対象)。 eclair: text-accent-primary。 */
  forecast: LightModeSemanticColors.Text.accentPrimary.value,
  /** 当日実績 (Area)。 */
  todayActual: DEMAND_FORECAST_TODAY_ACTUAL_AREA_COLOR,
  /** 実績トレンド予測。 design forecast-today.html:6045 `text-warning-high`。 */
  trendForecast: LightModeSemanticColors.Text.warningHigh.value,
  /** 気温 (予測)。 design forecast-today.html:6012 `text-negative-mid`。 */
  tempForecast: LightModeSemanticColors.Text.negativeMid.value,
  /**
   * 気温 (実績)。 design は予測と**同色**にして破線 / 実線の線種で区別する
   * (forecast-today.html:6012 と :6018 が同じ tempFcColor を使う)。
   */
  tempActual: LightModeSemanticColors.Text.negativeMid.value,
  /** 露点 (実績)。 design forecast-today.html:5543/:6029 `brand-teal`。 */
  dewpoint: LightModeSemanticColors.Brand.teal.value,
  /** 過去実績線 (最大 5 本)。 */
  past: DEMAND_FORECAST_PAST_COLORS,
  /** 確定履歴参照列線 (最大 5 本)。 */
  confirmRef: DEMAND_FORECAST_CONFIRM_RECORD_REF_COLORS,
} as const

// spec 準拠調整 (demand-forecast-daily-spec-align, AC-09) でプロバイダ比較線
// (旧 DEMAND_FORECAST_PROVIDER_COLORS) を削除した。Toolbar のプロバイダ切替
// チップ / KomaGrid 予測列 / ForecastChart 系列の連動削除に伴う撤去。

// ============================================================
// ガップ (pages/SupplyGap/models/types)
// ============================================================

/**
 * 系列色の安定マッピング。
 * 「集約」 表示の供給力合計 1 本 (design 集約表示 = supplyTotal、 charts.js L566:
 * brand-cyan)、「内訳」 表示の受渡区分カテゴリ (`category_code` → 色)、 ポジション、
 * 折れ線をここに集約する。 カテゴリ側のキーは backend が返す `category_code`
 * (`TradingDeliveryType.Code()` の SCREAMING_SNAKE + 市場由来の SPOT / INTRADAY +
 * 約定経路由来の BACKUP_REGULAR〈常時BU〉 / BASELOAD_MARKET / FORWARD_MARKET + 番兵 UNKNOWN_DELIVERY_TYPE) と一致させる。
 *
 * **同じ凡例に並ぶ系列に同じ色を割り当てない** (凡例が色で系列を識別するため)。
 * `types.test.ts`「同じ凡例に並ぶ系列の色が重複しない」 が構造的に固定している。
 *
 * 色は LightModeSemanticColors で解決できない箇所(Recharts fill)に使うため raw hex。
 * eclair token 対応コメントは各行末に記載。
 */
export const GAP_CATEGORY_COLOR_MAP: Record<string, string> = {
  SUPPLY_TOTAL: LightModeSemanticColors.Brand.cyan.value, // 供給力合計 — brand-cyan (#597eff)
  // 供給内訳 (AC-SDG-004b)。 相対 (受渡区分) → 市場 → 番兵 の順に色相を離す。
  //
  // 相対BG渡し / 相対JEPX渡し の割当は design (`supply/supply-gap.html` L599-600 の
  // `stackSeries`) が SoT: `bgDeliv = --brand-cyan` / `jepxDeliv = --brand-teal`。
  // BG が SUPPLY_TOTAL と同色 (brand-cyan) なのは design の意図で、 集約 → 内訳の
  // 切替でスタック最下段が同じシアンのまま残り、 棒の連続性が保たれる
  // (逆に振ると「同じシアンの棒」 が供給力合計から相対JEPX渡しへ化ける)。
  // SUPPLY_TOTAL と BG が同じ凡例に並ぶことは無い (集約 / 内訳は排他)。
  BG: LightModeSemanticColors.Brand.cyan.value, // 相対BG渡し — brand-cyan (#597eff)
  ECLEAR: LightModeSemanticColors.Brand.purple.value, // 相対eClear渡し — brand-purple (#bb97ff)
  JEPX: LightModeSemanticColors.Brand.teal.value, // 相対JEPX渡し — brand-teal (#00b0b2)
  // ベースロード市場 / 先渡し市場 — issue #8416 の Des 判断 (2026-09-28、案 B)。 eclair token が
  // 無いため 常時BU と同じく raw hex を直書きする。 design の旧割当 (brand-purple /
  // text-warning-high) は既存系列 (ECLEAR / INTRADAY) と衝突するため採らない。
  BASELOAD_MARKET: '#faaf9e', // ベースロード市場
  FORWARD_MARKET: '#f53df5', // 先渡し市場
  SPOT: LightModeSemanticColors.Brand.yellow.value, // スポット市場 — brand-yellow (#ffdd29)
  // 時間前市場 — eclair text-warning-high (#5d4e00)。 design (supply/supply-gap.html
  // L599-607) は brand-green を 常時BU に割り当てており、 時間前市場は design の
  // スタック系列に存在しない (実装固有カテゴリ)。 design 由来の割当を持つ 常時BU を
  // 優先し、 design に割当の無い側をここへ退避させた。
  INTRADAY: LightModeSemanticColors.Text.warningHigh.value,
  // 常時BU — マゼンタ (#b5179e、eclair token なし)。 issue #7422 の Des 判断
  // (2026-09-09 提起・2026-09-11 確定) により旧 brand-green (#69d582) から変更。
  // dark テーマで brand-green がロング (text-positive-mid) と同色化し、 凡例で
  // 常時BU とロングが判別できなかったため。 design 側 (`supply/supply-gap.html`)
  // も凡例チェックボックスの accent / チャート系列 / ツールチップの 3 箇所とも
  // `var(--brand-magenta, #b5179e)` に更新済み。 eclair token に brand-magenta が
  // 無いため、 このファイルの他の token なし色 (DEMAND_PLUS_SALES 等) と同じ raw
  // hex 直書きパターンに揃える。
  BACKUP_REGULAR: '#b5179e',
  UNKNOWN_DELIVERY_TYPE: LightModeSemanticColors.Text.lowEmphasis.value, // その他 — eclair text-low-emphasis
  LONG: LightModeSemanticColors.Text.positiveMid.value, // ロング — eclair text-positive-mid
  SHORT: LightModeSemanticColors.Text.negativeMid.value, // ショート — eclair text-negative-mid
  // 折れ線。 DEMAND_PLUS_SALES は旧 brand-pink (#ec4899) がダークモードでショート
  // (text-negative-mid #ff787a) と同色化し凡例で判別不能だったため、 design 同様
  // オレンジ (#f5821f) に変更してショート(赤系)から分離する。
  DEMAND_PLUS_SALES: '#f5821f', // 需要想定+販売 — eclair token なし (design 指定色)
  DEMAND_FORECAST: LightModeSemanticColors.Brand.blue.value, // 需要想定 — brand-blue (#0c00c5、design demandColor)
}

/**
 * 供給内訳の未知区分フォールバック色 (濃紺、 Tailwind sky-900 相当、 eclair token
 * なし = ドメイン固有色)。 `GAP_CATEGORY_COLOR_MAP` に無い `category_code`
 * (= 想定外の区分) は **すべてこの 1 色**で描く。
 *
 * 以前は 7 色 palette をコードのハッシュで循環させていたが、 producer が出す
 * `category_code` は `legCategoryLabel` の全分岐 + 市場の固定 const で 9 種に閉じ、
 * 9 種とも上の固定マップに載っているため到達する経路が無い (実際に起こりうる
 * 受渡区分の追加も番兵 `UNKNOWN_DELIVERY_TYPE` に倒れる)。 **同時に出た未知区分
 * どうしを色で区別しない**ことを受け入れて単一色へ畳んだ (#7837)。
 *
 * **固定マップのどの色とも十分に離す** — 上の「同じ凡例に並ぶ系列に同じ色を割り
 * 当てない」 は未知区分にも効いていなければ意味がない。 旧候補は `#14b8a6` が
 * brand-teal (相対JEPX渡し)、 `#8b5cf6` が brand-purple (相対eClear渡し)、
 * `#f97316` が需要想定+販売の `#f5821f` と **ほぼ同色** で、 未知区分が隣の既知
 * 区分と見分けられなかった。
 *
 * 2026-09-18 是正 (issue #7422): それまでこの値はマゼンタ (`#b5179e`) だったが、
 * Des 判断で 常時BU (`BACKUP_REGULAR`) の色として先に確定していたため、 未知区分
 * フォールバックと常時BU が同色になっていた (常時BU は固定マップに載っており
 * fallback 側には本来到達しないが、 値の重複自体が「固定マップのどの色とも
 * 十分に離す」 という上の原則に反する)。 常時BU にマゼンタを譲り、 fallback は
 * 固定マップの他候補 (`#0369a1` 系の青緑、 `#9333ea` 系の紫 等) より margin の
 * 大きい濃紺 `#0c4a6e` に差し替えた。
 *
 * 現在の色は固定マップの全色 (常時BU のマゼンタを含む) に対して redmean 距離
 * 160 以上を確保している (`models/supplyCategories.test.ts`「fallback 色が固定
 * マップの色と紛れない」 が構造的に固定)。 色を変える時は同テストを通すこと。
 */
export const GAP_FALLBACK_COLOR = '#0c4a6e'

// ============================================================
// ポジション (components/bidding/common/PositionChart)
// ============================================================

// spot.html `spotPosSeries` 準拠 (= --chart-2/5/3/6/1)。 colors 未指定時の default。
//
// executedBlock (--chart-5 赤紫) はスポット専用: 約定 (通常)/約定 (ブロック) の
// 積み上げ棒 2 色目 (AC-BSP-016、design `spot.html:3697` spotPosSeries)。
// 時間前市場 (INTRADAY_POSITION_COLORS) はブロック入札を持たないため定義しない。
//
// cover (「全札（下書き含む）」) / coverSubmitted (「入札済み」、 AC-BSP-108) は
// 同じ青系統で 2 段階の濃さに分ける (色分けの具体はデザイナー判断、 AC-BSP-108 注記)。
// coverSubmitted は既存の cover 色 (濃い青) をそのまま引き継ぎ (= 主要な数値として
// 既存ユーザーの見た目を変えない)、 cover (全札、 下書き含む広い方の線) は薄い青
// (= `BALANCE_DEMAND_SERIES_COLORS.demandActualCorrected` と同じ --chart-6 系統。
// 「主系列 = 濃色 / 補完系列 = 淡色」 という既存の色運用パターンに揃える)。
export const POSITION_SPOT_COLORS = {
  bar: 'executed' as const, // bar 系列 = 約定後カバー量
  barColor: '#009E73', // --chart-2 (緑) = 約定 (通常)
  executedBlock: '#CC79A7', // --chart-5 (赤紫) = 約定 (ブロック)
  need: '#E69F00', // --chart-3 (橙)
  cover: '#56B4E9', // --chart-6 (薄青、 全札〈下書き含む〉)
  coverSubmitted: '#0072B2', // --chart-1 (濃青、 入札済み)
}

// 時間前市場 (BidCreateTab) から colors prop で渡す。 spot と違い bar / need は semantic text
// token を使う (= 濃緑 / 濃橙)。 cover / coverSubmitted は spot と同じ --chart-6 / --chart-1
// (design `intraday.html` `intraPosSeries`: 2 画面で色が違うと同じ指標に見えないため)。
//
// remain (入札後残量) は AC-BIN-012 / AC-BIN-012a (2026-09-02 用語統一) で
// executed (約定後カバー量) に代わるバー系列として追加された指標で、 design
// `intraday.html` の `intraPosSeries` (executed バー) はまだ追随できておらず
// stale (design 是正は別レーン)。 spec が定める 3 系列 (入札時カバー量 / 調達必要量 /
// 入札後残量) を優先し、 旧 executed のバー色 (--text-positive-mid) をそのまま
// remain のバー色として引き継ぐ (need / cover の色は変更しない)。
export const POSITION_INTRADAY_COLORS = {
  bar: 'remain' as const, // bar 系列 = 入札後残量
  barColor: LightModeSemanticColors.Text.positiveMid.value, // --text-positive-mid (緑)
  need: LightModeSemanticColors.Text.warningHigh.value, // --text-warning-high (濃橙)
  cover: '#56B4E9', // --chart-6 (薄青、 全札〈下書き含む〉)
  coverSubmitted: '#0072B2', // --chart-1 (濃青、 入札済み)
}

/** --chart-4: 区分不明の札 (#10084)。 */
export const POSITION_UNKNOWN_ISSUER_COLOR = '#D55E00'

/** --border-low: grid / tooltip border */
export const POSITION_GRID_COLOR =
  LightModeSemanticColors.Border.lowEmphasis.value

/** --border-high: axis / tick line */
export const POSITION_AXIS_COLOR =
  LightModeSemanticColors.Border.highEmphasis.value

/**
 * GC reference line。 design/bidding/intraday.html:1576 が token ではなく raw red (#dc2626) を
 * 直指定しているための恒久例外 [DS差異] (D-3 監査で design ソースを確認済み、token 化しない)。
 */
export const POSITION_GC_COLOR = '#dc2626'

// tooltip 用 token
/** --surface-primary */
export const POSITION_TOOLTIP_BG_COLOR =
  LightModeSemanticColors.Surface.primary.value
/** --border-low */
export const POSITION_TOOLTIP_BORDER_COLOR =
  LightModeSemanticColors.Border.lowEmphasis.value
/** --text-positive-mid */
export const POSITION_TOOLTIP_POSITIVE_COLOR =
  LightModeSemanticColors.Text.positiveMid.value
/** --text-warning-mid */
export const POSITION_TOOLTIP_WARNING_COLOR =
  LightModeSemanticColors.Text.warningMid.value

// ============================================================
// スポット価格 (constants/spotPrice)
// ============================================================

/**
 * chart-5 (#CC79A7 赤紫) は LightModeSemanticColors に対応 key が無いため
 * eclair token 値をハードコード (コメントで token 名を併記)。 --chart-5 (eclair token)。
 */
export const SPOT_PRICE_CHART_5_COLOR = '#CC79A7'

// ============================================================
// 発電 (pages/BalanceGeneration/components/BgChart /
//        pages/BalanceGenerationDetail/components/DetailChart)
// ============================================================

// 発電形式 9 区分 (HJKS 準拠、conventions.md「発電形式」節) の色は
// **個人設定の既定色 `DEFAULT_COLORS`** (`~/constants/generationSource`、
// Settings と BalanceGeneration が共有する SoT。2026-09-11 昇格) が唯一の SoT
// (同 module の `GENERATION_SOURCE_LIST` が順序付きで束ねる)。
// 個人設定で 9 区分の色をカスタマイズする画面が既にあり、
// 独立した literal を chartPalette 側にも持つと食い違う (2026-09-10 是正:
// 旧 `GENERATION_SOURCE_COLORS` はここに独立 literal を持ち、 個人設定と
// 一部食い違っていた)。

/**
 * 発電一覧 (BgChart) の 計画 / 実績 系列色。 design `balance/hatsu.html` の
 * `.switch-item[data-color]` (genPlan = surface-accent-primary / genActual = brand-teal)。
 *
 * 8080 は switch とチャート (`js/charts.js` renderHatsuChart) で色定義が食い違っており、
 * チャート側は genActual を `text-positive-mid` = **風力と同じ色**で描いていた
 * (`charts.js:1429` だけ名前付き変数を経由せず Line に直書き = 書き漏れに見える)。
 * 実装もそれを引き継いでいたため、 フィルタ switch を系列色にすると
 * 「発電実績値」 と「風力」 が見分けられなくなる。
 *
 * switch 定義の方は 7 系列すべてが別色になるよう意図的に振られているので、
 * **switch 側を正としてチャートを合わせる** (2026-08-25 デザイナー判断)。
 * 8080 プロトの renderHatsuChart 側も同時に追随させた。
 *
 * genActual は 2026-09-14 の Des 判断 (#7809) で brand-teal から離した。
 * 9 区分化 (#7537) で「地熱」が増え、 その既定色 (同じく brand-teal) と
 * 衝突したため。 **地熱の側を動かさない**のは、 地熱の light 値が #4966 の
 * A11Y 是正で確定済みで、 積み上げ帯には非テキスト 3:1 が効くため条件を
 * 満たす色がほとんど残らないから。 発電実績値は 9 区分の分類パレットとは
 * 別軸の集計線で、 brand-teal を借りていたのは地熱が無かった 5 区分時代の
 * 名残なので、 動かすならこちら側という判断 (2026-08-25 の判断も地熱を
 * 想定していない)。
 */
export const GENERATION_LIST_SERIES_COLORS = {
  genPlan: LightModeSemanticColors.Surface.accentPrimary.value,
  // eclair token なし (= 種別パレットが使う色相帯の外へ Des が振った値。
  // prototype `--gen-series-actual` と同値、ebalance-prototype#325)。
  // 単一値で light・dark 両テーマ成立するのでテーマ別値は持たせない。
  genActual: '#ff2989',
} as const

/** 発電BG詳細 (DetailChart) の 計画 / 実績 系列色。 design `js/charts.js` renderHatsuChart。 */
export const GENERATION_DETAIL_SERIES_COLORS = {
  genPlan: LightModeSemanticColors.Brand.cyan.value,
  genActual: LightModeSemanticColors.Text.positiveMid.value,
} as const

// BgChart tooltip の発電源バッジ色は Bar の fill と同じ `GENERATION_SOURCE_LIST`
// (`~/constants/generationSource`) の `color` フィールドを直接参照する
// (バッジは「チャートのどの面か」を指す凡例なので、面と別の色を持たせると凡例と
// して成立しない。design `js/charts.js:1377-1387` の `bfn()` も Bar と同じ変数を
// バッジ背景に渡す作法)。

// ============================================================
// 同時同量 BG 詳細 気象チャート
// (pages/BalanceDemandDetail/components/WeatherChart)
// design/balance/bg-region.html: 気温=text-negative-mid / 露点=brand-teal /
// 降水量=brand-cyan (data-series="precipitation" data-color="brand-cyan"。
// 2026-08-15 是正: 旧コメント「降水量=棒(text-link)」は stale — 需要チャート側の
// 降水量トグル/描画/ツールチップ (BALANCE_DEMAND_SERIES_COLORS.precipitation) と
// 揃える brand-cyan が design 側の一貫した正値)。
// ============================================================

/** 実績気温 折れ線 (左軸 ℃)。 eclair: text-negative-mid。 */
export const BALANCE_WEATHER_TEMP_COLOR =
  LightModeSemanticColors.Text.negativeMid.value

/** 実績露点 折れ線 (左軸 ℃)。 eclair: brand-teal。 */
export const BALANCE_WEATHER_DEW_COLOR =
  LightModeSemanticColors.Brand.teal.value

/**
 * 実績降水量 棒 (右軸 mm)。 eclair: brand-cyan (design data-color="brand-cyan")。
 * 需要チャート側の降水量系列 (BALANCE_DEMAND_SERIES_COLORS.precipitation) と
 * 同値 — 同一系列が上下チャートで別色になるのを防ぐ (2026-08-15 是正。旧値
 * #3b82f6 はトグル/RegionChart bar/ツールチップの 3 者とも食い違っていた)。
 */
export const BALANCE_WEATHER_PRECIP_COLOR =
  LightModeSemanticColors.Brand.cyan.value

// ============================================================
// 同時同量 需要チャート 系列色
// (系列トグル: pages/BalanceDemand・BalanceDemandDetail の FilterCard /
//  チャート描画: RegionChart・DetailChart /
//  ホバーツールチップ: components/balance/DemandTooltip — 3 箇所共通)
// design/balance/bg.html・bg-region.html の .switch-item[data-color] と
// design/js/charts.js renderBgChart の tooltip バッジ色に準拠。
// ============================================================

/**
 * 電圧階級別 (特別高圧/高圧/低圧) の系列色。 積み上げ帯・系列トグルのトラック色・
 * ツールチップのバッジ色が **同一の値を参照する** ための単一定義 (doji-bg.md
 * AC-BDB-031「トグル自体が系列の凡例を兼ねる」)。 3 箇所に別々に色を書くと、
 * 凡例と実描画がずれても型では検出できない。
 *
 * 値は Des 確定色 (issue #5018 の配色重複解消 + issue #5201 の A11Y 非テキスト
 * コントラスト 3:1 是正を統合した最終値)。 8080 プロト `css/tokens.css` L262-264 の
 * `--volt-class-extra-high` / `--volt-class-high` / `--volt-class-low` と同値。
 *
 * [DS差異] ecloud の semantic token に該当色が無い (A11Y のために個別調整した
 * 値のため) ので raw hex を直指定する。 旧実装は accent-primary / brand-yellow /
 * text-positive-mid を流用していたが、 特別高圧が「需要実績値」と、 低圧が
 * 「実績値(補完)」と同色になる重複があり (#5018)、 かつ light で 2/3 が 3:1 未達
 * だった (#5201)。
 *
 * dark 値 (#F58A40 / #2CBA93 / #DE91B7) は 8080 に定義済みだが、 dev 側は
 * `index.tsx` の `forcedTheme="light"` でテーマ切替を持たないため入れない。
 */
export const BALANCE_VOLTAGE_CLASS_COLORS = {
  /** 特別高圧 (8080 `--volt-class-extra-high`。 3.17:1 -> 4.47:1)。 */
  voltageExtraHigh: '#a54700',
  /** 高圧 (8080 `--volt-class-high`。 2.83:1 -> 3.72:1)。 */
  voltageHigh: '#03825e',
  /** 低圧 (8080 `--volt-class-low`。 2.54:1 -> 3.13:1)。 */
  voltageLow: '#b76593',
} as const

/**
 * 電圧階級別の積み上げ帯どうしの境界線色 = **そのチャートが載るカードの地色**。
 * 地色で 1px 抜くことで、 隣り合う帯が溶けずに 3 本の帯として読める
 * (design `js/charts.js` の `_cVoltSep` = `cssVar('surface-primary')`)。
 *
 * 最上段の帯だけはこの色を使わず自色の輪郭 1.5px を持たせる — 地色で抜くと
 * スタックの外周が消えて帯がカードに溶けるため。 最上段の判定は
 * `~/models/voltageBreakdown` の `topVoltageClass`。
 *
 * 区切り線は「その画面の実際の地色」に合わせないと、 抜いたようには見えず
 * **線が地の上に浮く**。 したがってカードの地色を変える時は必ずこの値も一緒に変える。
 *
 * 2026-09-07 に一覧カードの地色を design へ揃えた (カード = `Surface.primary` /
 * ヘッダのみ `Surface.secondary`。 issue #7064)。 それまで実装はカード全面が
 * `Surface.secondary` で design と逆転しており、 ここもその現状に合わせていた。
 * **いまは一覧・詳細とも `Surface.primary` で揃っている**が、 「カードの地色」 という
 * 意味は画面ごとに独立なので定数は 2 つのまま残す (将来どちらかだけ変わりうる)。
 */
export const BALANCE_VOLTAGE_BAND_SEPARATOR_COLOR = {
  /** 一覧カード (`pages/BalanceDemand` の RegionChart)。 カード本体は Surface.primary。 */
  regionCard: LightModeSemanticColors.Surface.primary.value,
  /** 詳細チャート (`pages/BalanceDemandDetail` の DetailChart)。 地は Surface.primary。 */
  detailChart: LightModeSemanticColors.Surface.primary.value,
} as const

/**
 * 需要チャート系列色の単一 SoT。 系列トグル (基本表示/分析補助/詳細) の ON
 * トラック色・チャートの描画色・ツールチップのバッジ色が **同一の値を参照する**
 * ための単一定義 (doji-bg.md AC-BDB-031「トグル自体が系列の凡例を兼ねる」)。
 *
 * 旧実装は BALANCE_SERIES_TOGGLE_COLORS (トグル用) / BALANCE_DEMAND_TOOLTIP_COLORS
 * (ツールチップ用) の 2 定数に分かれ、かつチャート描画側は個別に raw hex を
 * 直書きしていたため、3 箇所 (トグル/チャート/ツールチップ) が値として食い違って
 * いても型では検出できなかった (2026-08-15 是正。design/js/charts.js:730-734 の
 * 2026-08-04 修正コメントが指摘する drift と同種)。1 定数へ統合し、3 箇所とも
 * この定数だけを参照する構造にすることで drift を構造的に防ぐ。
 *
 * data-color 無しの switch (天気アイコン) は既定色 (accent-primary) なのでここには
 * 持たない。 電圧階級別の 3 系列は BALANCE_VOLTAGE_CLASS_COLORS が単独の SoT
 * (積み上げ帯の fill にも直接使うため定義を分けている) — ここでは同値を spread
 * で取り込み、ツールチップ側の参照点を 1 つに保つ。
 */
export const BALANCE_DEMAND_SERIES_COLORS = {
  /** 需要計画値。 eclair: brand-cyan (design js/charts.js planColor)。
   *  旧ツールチップ実装は accent-primary だったが、design 自身の 2026-08-04
   *  是正原則 (トグル=凡例) と矛盾する残存 drift だったため [design差異] として
   *  トグル/チャート線と同じ brand-cyan に統一する。 */
  demandPlan: LightModeSemanticColors.Brand.cyan.value,
  /** 需要実績値。 eclair token なし (design --chart-1 青)。実績値(補完)
   *  (chart-6 スカイブルー) と重ねて描くため、design どおりの濃い青でないと
   *  両系列が同系色に潰れて補完分を判別できない。 */
  demandActual: '#0072B2',
  /** 実績値(補完)。 eclair token なし (design --chart-6 スカイブルー)。 */
  demandActualCorrected: '#56B4E9',
  /** 気温/予想気温。 eclair: text-negative-mid。 */
  temperature: LightModeSemanticColors.Text.negativeMid.value,
  /** 降水量。 eclair: brand-cyan (design js/charts.js rainColor)。
   *  旧実装はトグル無配色・RegionChart bar が brand-teal・ツールチップ/下段
   *  気象チャートが #3b82f6 の 3 者バラバラだった (2026-08-15 是正)。 */
  precipitation: LightModeSemanticColors.Brand.cyan.value,
  /** 調達量・販売量。 eclair: text-positive-mid。 */
  procurementNet: LightModeSemanticColors.Text.positiveMid.value,
  /** 露点/予想露点。 eclair: brand-teal。 */
  dewPoint: LightModeSemanticColors.Brand.teal.value,
  // 電圧階級別 3 系列は積み上げ帯・トグルと同値でなければ凡例として機能しないため、
  // BALANCE_VOLTAGE_CLASS_COLORS から取る (ここで別途 hex を書かない)。
  ...BALANCE_VOLTAGE_CLASS_COLORS,
} as const

/**
 * 需要チャートの低圧未着のコマの網掛け (斜線)。 一覧カード (RegionChart)・詳細
 * (DetailChart)・凡例の見本で同じ見た目にする。 値は design `js/charts.js` の
 * `lv-pending-hatch` (45°・間隔 6px・太さ 1.5px・色 `--border-mid`・枠なし、 #11269)。
 */
export const BALANCE_AWAITING_VOLTAGE_HATCH = {
  className: 'chart-awaiting-voltage-area',
  color: LightModeSemanticColors.Border.midEmphasis.value,
  spacing: 6,
  strokeWidth: 1.5,
} as const
